/**
 * LlmSuggestionButton — Button für Mistral-LLM-Vorschlag pro Feld.
 *
 * Aufruf-Sequenz (alle Schritte erzwungen):
 *   1. Disable + Spinner
 *   2. pseudonymize(currentValue + heuristicsContext) → safe text
 *   3. buildLlmPrompt(...) → system + user prompt
 *   4. callMistral(...) → MistralResponse
 *   5. Confirm "Überschreiben?" bei nicht-leerem Feld
 *   6. onSuggest(rehydrate(response.content, mapping))
 *   7. Re-enable
 *
 * Fehler werden über onError zurückgegeben — Aufrufer mappt sie auf Toast.
 *
 * Sichtbarkeit: NUR rendern wenn useLlmFeatureState().available === true.
 * Das wird vom Aufrufer per `{available && <LlmSuggestionButton/>}`
 * sichergestellt — die Komponente nimmt das nicht selbst in die Hand,
 * damit der Render-Pfad ohne aktiviertes Flag gar nicht gemountet wird.
 *
 * Pseudonymisierung MUSS hier passieren — der mistralClient hat einen
 * PII-Pattern-Re-Check als defense-in-depth, aber wenn der greift, ist
 * es ein Bug. Wir vertrauen NICHT auf den Re-Check als primären Schutz.
 */

import { useState } from 'react';
import { Sparkles } from 'lucide-react';

import { pseudonymize, rehydrate, type PseudonymizationContext } from '../../lib/llm/pseudonymize';
import { buildLlmPrompt, type LlmFieldKey } from '../../lib/llm/buildLlmPrompt';
import { callLlm, LlmError, LlmErrorCode, getActiveProviderLabel } from '../../lib/llm/llmClient';
import type { LlmErrorCodeValue } from '../../lib/llm/llmClient';
import type { ScoreResult } from '@tragfaehigkeit/core';
import { useConfirm } from '../ui/ConfirmDialog';
import { de } from '../../lib/i18n/de';

interface LlmSuggestionButtonProps {
  /** Welches Bewertungstexte-Feld soll bedient werden? */
  readonly fieldKey: LlmFieldKey;
  /** Aktueller Text im Feld — für Überschreiben-Dialog + als Kontext. */
  readonly currentValue: string;
  /**
   * Fachlicher Datenkontext (Heuristik-Output, Branche, Score-Hintergrund).
   * Wird VOR dem Call pseudonymisiert. Aufrufer baut das aus statement-
   * Heuristik + Stammdaten.
   */
  readonly heuristicsContext: string;
  /** Score-Ergebnis für Ampel + Score-Wert im Prompt. */
  readonly scoreResult: ScoreResult;
  /** Pseudonymisierungs-Kontext aus Case + ClientStaticData. */
  readonly pseudonymizationContext: PseudonymizationContext;
  /**
   * Case-ID für Audit-Log + llm_usage_log. Im Demo-Modus null
   * (caseId='demo' — KEINE UUID, also case_id in DB = null).
   */
  readonly caseId: string | null;
  /** Callback wenn der Berater den Vorschlag akzeptiert. */
  readonly onSuggest: (text: string) => void;
  /**
   * Optional: Status-Callback für UI-Toast/Hinweis. Wird mit deutschen
   * Texten aufgerufen — Aufrufer schaltet flashSuggestion(...) o.ä.
   */
  readonly onStatus?: (message: string, kind: 'info' | 'success' | 'error') => void;
}

/**
 * Mapped LlmError-Codes auf deutsche, behördentaugliche Berater-Texte.
 *
 * Provider-agnostisch — die UI muss nicht wissen ob Claude oder Mistral
 * gerade aktiv ist. Der Provider-Name kommt im "configured"-Hinweis vor.
 *
 * Exportiert für Tests.
 */
export function mapLlmErrorToGermanMessage(code: LlmErrorCodeValue): string {
  switch (code) {
    case LlmErrorCode.LLM_FLAG_DISABLED:
      return 'Das KI-Vorschlags-Feature wurde gerade deaktiviert. Bitte Seite neu laden.';
    case LlmErrorCode.LLM_NOT_CONFIGURED:
      return 'Kein LLM-Provider im System konfiguriert. Bitte Administrator informieren.';
    case LlmErrorCode.PII_DETECTED:
      return 'Vorschlag konnte nicht generiert werden — die Eingabe enthielt möglicherweise personenbezogene Daten, die nicht an den LLM-Provider übertragen werden dürfen. Bitte Berater-Notizen prüfen und Klarnamen entfernen.';
    case LlmErrorCode.HTTP_ERROR:
      return 'LLM-API ist aktuell nicht erreichbar. Bitte später erneut versuchen.';
    case LlmErrorCode.TIMEOUT:
      return 'Anfrage hat zu lange gedauert. Bitte erneut versuchen.';
    case LlmErrorCode.PARSE_ERROR:
      return 'Antwort vom LLM-Provider war nicht verarbeitbar. Bitte mit Heuristik-Vorschlag arbeiten.';
    case LlmErrorCode.PROVIDER_UNKNOWN:
      return 'Unbekannter LLM-Provider konfiguriert. Bitte Administrator informieren.';
    default:
      return 'Unbekannter Fehler bei der KI-Generierung. Bitte mit Heuristik-Vorschlag arbeiten.';
  }
}

/** Backward-compat: Re-Export unter altem Namen für Tests. */
export const mapMistralErrorToGermanMessage = mapLlmErrorToGermanMessage;

export function LlmSuggestionButton({
  fieldKey,
  currentValue,
  heuristicsContext,
  scoreResult,
  pseudonymizationContext,
  caseId,
  onSuggest,
  onStatus,
}: LlmSuggestionButtonProps) {
  const confirm = useConfirm();
  const [isPending, setIsPending] = useState<boolean>(false);

  const providerLabel = getActiveProviderLabel();

  async function handleClick(): Promise<void> {
    if (isPending) return; // callLlm ist nicht idempotent — Klick-Spam blocken
    setIsPending(true);
    try {
      onStatus?.(`Pseudonymisiere Daten und kontaktiere ${providerLabel} …`, 'info');

      // 1. Pseudonymisieren — PFLICHT VOR LLM-Call
      const rawContext = composeRawContext(currentValue, heuristicsContext);
      const { content: pseudoContext, mapping } = pseudonymize(rawContext, pseudonymizationContext);

      // 2. Prompt bauen — pseudoContext fließt in den User-Prompt
      const { systemPrompt, userPrompt } = buildLlmPrompt({
        heuristicsContext: pseudoContext,
        scoreResult,
        fieldKey,
      });

      // 3. LLM aufrufen (Dispatcher entscheidet Claude vs. Mistral)
      const response = await callLlm(systemPrompt, userPrompt, {
        caseId,
        fieldKey,
      });

      // 4. Rehydrate (Mandant:in → Klarname für Berater-Sicht)
      const finalText = rehydrate(response.content, mapping);

      // 5. Überschreiben-Bestätigung
      if (currentValue.trim().length > 0) {
        const proceed = await confirm({
          title: de.confirmDialog.overwriteTitle,
          body: de.confirmDialog.llmOverwriteBody,
          confirmLabel: de.confirmDialog.overwriteConfirmLabel,
        });
        if (!proceed) {
          onStatus?.('Vorschlag verworfen — bestehender Text bleibt erhalten.', 'info');
          return;
        }
      }

      onSuggest(finalText);
      onStatus?.('KI-Vorschlag übernommen — bitte fachlich prüfen und ggf. anpassen.', 'success');
    } catch (err) {
      if (err instanceof LlmError) {
        onStatus?.(mapLlmErrorToGermanMessage(err.code), 'error');
      } else {
        const msg = err instanceof Error ? err.message : 'Unbekannter Fehler.';
        onStatus?.(`KI-Generierung fehlgeschlagen: ${msg}`, 'error');
      }
    } finally {
      setIsPending(false);
    }
  }

  // Provider-Label kürzen für Button (Vollform im title-Tooltip)
  const shortLabel = providerLabel.replace(/\s*\([^)]*\)\s*/g, '');
  const buttonText = isPending ? 'Generiert …' : `KI-Vorschlag (${shortLabel})`;

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        void handleClick();
      }}
      aria-label={`KI-Vorschlag generieren für Feld ${fieldKey}`}
      title={`Vorschlag von ${providerLabel} generieren. Daten werden pseudonymisiert übertragen.`}
      className="inline-flex items-center gap-1 rounded-sm border border-stone-400 bg-stone-50 px-2 py-1 font-sans text-[10px] font-medium uppercase tracking-widest text-stone-700 transition-colors hover:border-stone-900 hover:bg-stone-900 hover:text-white disabled:cursor-not-allowed disabled:border-stone-200 disabled:bg-stone-100 disabled:text-stone-400"
    >
      <Sparkles size={11} aria-hidden="true" />
      {buttonText}
    </button>
  );
}

/**
 * Kombiniert den bestehenden Feld-Text und den Heuristik-Kontext zu einem
 * Roh-Text, der dann pseudonymisiert wird. Bewusst exportiert für Tests.
 */
export function composeRawContext(currentValue: string, heuristicsContext: string): string {
  const cur = currentValue.trim();
  const heur = heuristicsContext.trim();
  if (cur.length === 0) return heur;
  if (heur.length === 0) return cur;
  return `Bisheriger Text:\n${cur}\n\nHeuristik-Vorbefüllung und Kontext:\n${heur}`;
}
